import { useEffect, useRef } from 'react'
import { analytics, type CalculatorType } from '@/lib/analytics'

/**
 * Fires `calculator_started` exactly once per mount, on the user's first
 * meaningful form interaction with a GATED calculator (ZIP/region-gated or
 * explicit-submit). For those tools `calculator_used` cannot fire until a
 * downstream gate resolves (a valid ZIP, a submitted lab report), so a user
 * who starts entering but never clears the gate is invisible today. This
 * captures that pre-gate start so `calculator_started` -> `calculator_used`
 * becomes a real abandonment funnel.
 *
 * Attach the returned ref to a DOM element that CONTAINS the tool's inputs
 * (the page-content root is fine — the listener only reacts to `input` /
 * `change`, which only bubble from real form fields, so wrapping extra chrome
 * costs nothing and taps/scrolls never count).
 *
 * Do NOT use on live-useMemo tools (lime, topsoil, sod, mowing, cost, etc.):
 * they already fire `calculator_used` (source: 'live_update') on the first
 * keystroke, so `started` would just duplicate `used`. Map + soil-temperature
 * have their own start events (lawn_draw_started / soil_temp_search_started).
 */
/**
 * `calculator_started` means: the first meaningful field interaction of a tool
 * visit. Once per (pathname, mount), for EVERY tool — gated or live. It is the
 * denominator for `calculator_used`, which is not once-per-visit (it repeats on
 * live recompute and can fire with no human input at all from source
 * 'url_params' | 'saved_profile' | 'quick_start'). Compare the two by unique
 * person or session, never by raw event count.
 *
 * Module-scope guard so the route-level delegate (components/tools/
 * calculator-started-listener.tsx) and any per-tool call of this hook resolve to
 * exactly one emission per visit. The delegate is an ancestor capture listener,
 * so within one event dispatch it always claims the guard first.
 */
let startedForPath: string | null = null

export function markCalculatorStarted(
  calculatorType: CalculatorType,
  pathKey: string,
): boolean {
  if (startedForPath === pathKey) return false
  startedForPath = pathKey
  analytics.calculatorStarted(calculatorType)
  return true
}

/**
 * Clears the once-per-visit guard. The delegate calls this at the top of every
 * effect run (mount or pathname change), which is what makes the guard mean
 * "once per visit" rather than "once per distinct path" — without it, A -> B ->
 * A suppresses A's second visit.
 */
export function resetCalculatorStartedGuard(): void {
  startedForPath = null
}

/** Test-only alias of resetCalculatorStartedGuard. */
export function __resetCalculatorStartedGuard(): void {
  resetCalculatorStartedGuard()
}

/**
 * Opt-out marker for shared non-calculator input surfaces that render on tool
 * routes (newsletter modal, result email capture, soil-temp ZIP alert). Their
 * roots carry `data-calc-ignore`; typing there is not starting a calculator.
 */
export const CALC_IGNORE_SELECTOR = '[data-calc-ignore]'

export function isCalculatorIgnoredEvent(event: Event): boolean {
  const target = event.target
  return target instanceof Element && target.closest(CALC_IGNORE_SELECTOR) !== null
}

export function useCalculatorStarted<T extends HTMLElement = HTMLDivElement>(
  calculatorType: CalculatorType,
) {
  const containerRef = useRef<T | null>(null)
  const firedRef = useRef(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const fire = (event: Event) => {
      if (firedRef.current) return
      if (isCalculatorIgnoredEvent(event)) return
      const pathKey =
        typeof window === 'undefined' ? 'ssr' : window.location.pathname
      // Detach only on a successful mark. A rejected mark means the delegate
      // already emitted for this visit, but staying attached costs nothing and
      // cannot strand a visit that never emitted.
      if (!markCalculatorStarted(calculatorType, pathKey)) return
      firedRef.current = true
      detach()
    }

    // Capture phase so a field that stops propagation still counts. `input`
    // covers text/number/range typing; `change` covers native <select> and
    // commit-on-blur fields. Deliberately NOT pointerdown/click — those fire on
    // any tap and would over-count "started".
    const detach = () => {
      el.removeEventListener('input', fire, true)
      el.removeEventListener('change', fire, true)
    }
    el.addEventListener('input', fire, true)
    el.addEventListener('change', fire, true)

    return detach
  }, [calculatorType])

  return containerRef
}
